import { WSQueries } from "../../../../query/WSQuery";
import { useEngagementListQuery } from "../../../../query/engagements/queries/useEngagementListQuery";
import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { EngagementRowsTable } from "../../components/EngagementRowsTable";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { useCreateEngagementModal } from "../../modals/CreateEngagementModal";
import { selectorInfinityEngagements } from "../../../Payees/selectors/selectorInfinityEngagements";
import React from "react";
import { Tabs } from "../../../../components/Tabs";
import { getSectionPathByEngagementStatus } from "../../paths";
import { useEngagementListSizeQuery } from "../../../../query/engagements/queries/useEngagementListSizeQuery";
import { EngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import { Route, Switch } from "react-router-dom";

const EngagementTypesTab: React.FC<{
  engagementStatus: EngagementStatus;
  isMainTab?: boolean;
}> = ({ engagementStatus, isMainTab }) => {
  const modalCreateEngagement = useCreateEngagementModal();
  const queryEngagementList = useEngagementListQuery({
    filter: {
      isDefault: false,
      status: engagementStatus
    },
    sort: {
      // status: "asc",
      updatedAt: "desc"
    }
  });

  return (
    <WSQueries
      queries={{
        queryEngagementList
      }}
    >
      {({ queryEngagementListData }) => {
        const { list, listSize } = selectorInfinityEngagements(
          queryEngagementListData.pages
        );

        return (
          <>
            <WSFilters
              my="L"
              values={{}}
              onFilter={newValues => {}}
              info={{
                count: listSize
              }}
              quickFilters={[]}
            />
            {list.length === 0 ? (
              <WSEmptyState
                py="3XL"
                hasBorder
                type="documents"
                title="No engagement types yet"
                bgColor="gray"
                description={
                  isMainTab
                    ? "Once an engagement type is added, they will appear here."
                    : undefined
                }
                actions={
                  isMainTab
                    ? {
                        buttons: [
                          {
                            label: "Add engagement",
                            kind: "Secondary",
                            onClick: async () => {
                              await modalCreateEngagement.open();
                            }
                          }
                        ]
                      }
                    : undefined
                }
              />
            ) : (
              <WSInfiniteScroll
                mt="M"
                onLoad={() => {
                  queryEngagementList.fetchNextPage();
                }}
                loadMore={list.length > 0}
                endOfList={!queryEngagementList.hasNextPage}
                loading={!!queryEngagementList.isFetchingNextPage}
              >
                <EngagementRowsTable engagements={list} />
              </WSInfiniteScroll>
            )}
          </>
        );
      }}
    </WSQueries>
  );
};

export const RouteEngagementTypes: React.FC = () => {
  useBrowserPageTitle("Engagement types");

  const modalCreateEngagement = useCreateEngagementModal();

  const queryActiveEngagementTypeSummary = useEngagementListSizeQuery({
    filter: {
      isDefault: false,
      status: EngagementStatus.Active
    }
  });

  const queryArchivedEngagementTypeSummary = useEngagementListSizeQuery({
    filter: {
      isDefault: false,
      status: EngagementStatus.Inactive
    }
  });

  return (
    <WSPage>
      <WSPageToolbar
        title={"Engagement types"}
        children={
          <WSText.ParagraphSm color="gray500" style={{ maxWidth: "550px" }}>
            Engagements types represent a defined scope of work—like a project,
            or department—that can be assigned to contractors and can include
            specific requirements.
          </WSText.ParagraphSm>
        }
        actions={[
          {
            label: "Add engagement type",
            onClick: async () => {
              await modalCreateEngagement.open();
            },
            buttonKind: "Primary"
          }
        ]}
      />

      <WSQueries
        queries={{
          queryActiveEngagementTypeSummary,
          queryArchivedEngagementTypeSummary
        }}
      >
        {({
          queryActiveEngagementTypeSummaryData,
          queryArchivedEngagementTypeSummaryData
        }) => {
          const tabs = [
            {
              label: "Active",
              path: getSectionPathByEngagementStatus(EngagementStatus.Active),
              badge: queryActiveEngagementTypeSummaryData,
              engagementStatus: EngagementStatus.Active,
              isMainTab: true
            },
            {
              label: "Archived",
              path: getSectionPathByEngagementStatus(EngagementStatus.Inactive),
              badge: queryArchivedEngagementTypeSummaryData,
              engagementStatus: EngagementStatus.Inactive
            }
          ];

          return (
            <>
              <Tabs mb="L" tabs={tabs} />
              <Switch>
                {tabs.map(tab => (
                  <Route
                    key={tab.path}
                    path={tab.path}
                    render={() => (
                      <EngagementTypesTab
                        engagementStatus={tab.engagementStatus}
                        isMainTab={tab.isMainTab}
                      />
                    )}
                  />
                ))}
              </Switch>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
